<template>
  <div v-if="firstOffer" v-bind="$attrs" class="space-y-12 md:space-y-20">
    <TripSwapContentHotelCard
      v-if="offers.current"
      :offer="offers.current"
      isCurrent
      @show-details="
        (value) => {
          showHotelDetail(value)
          isCurrenSelected = true
        }
      "
    />

    <TripSwapContentHotelCard
      v-if="offers.offered"
      :offer="offers.offered"
      @show-details="
        (value) => {
          showHotelDetail(value)
          isCurrenSelected = false
        }
      "
    >
      <div
        class="absolute left-1/2 top-0 z-1 -mt-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-4 overflow-hidden rounded-md border border-primary bg-white px-6 py-4 text-base font-medium text-primary after:absolute after:inset-0 after:bg-primary/10 after:content-[''] md:gap-8 md:px-16 md:py-12 md:text-subject"
      >
        {{ $t('words.save') }} {{ totalSavedPrice }}
        <JIcon
          name="arrow-right"
          class="rotate-90"
          :size="isMobile ? 14 : 16"
        />
      </div>
    </TripSwapContentHotelCard>
  </div>

  <ServerError v-if="isError" />
  <JDrawer v-else v-model="hotelDetailsIsOpen" floatClose>
    <HotelDetail
      :relevantRoom="
        isCurrenSelected
          ? offers.current?.rooms[0].roomCode
          : offers.offered?.rooms[0].roomCode
      "
      showAvailability
      :allowRoomSelection="false"
      :isLoading="hotelInfoIsFetching"
    />
  </JDrawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { JDrawer, JIcon } from '@journey/ui-kit'
import TripSwapContentHotelCard from './TripSwapContentHotelCard/TripSwapContentHotelCard.vue'
import type { Props } from './TripSwapContentHotel.types'
import { useCurrency, useDevicePlatform } from '@/modules/Core/composables'
import ServerError from '@/modules/Core/components/shared/ServerError/ServerError.vue'
import HotelDetail from '@/modules/Core/components/shared/HotelDetail/HotelDetail.vue'
import featureFlag from '@/config/featureFlag'
import { useHotelInfoQuery } from '@/modules/Search/requests/queries'
import useHotelSearchResultsStore from '@/modules/Search/store/hotelSearchResults'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<Props>()

const { formatCurrency } = useCurrency()

const { isMobile } = useDevicePlatform()

const firstOffer = computed(() => props.data.hotelOffers?.[0])

const offers = computed(() => {
  return {
    current: firstOffer.value?.currentBooking ?? null,
    offered: firstOffer.value?.offeredBooking ?? null,
  }
})

const totalSavedPrice = computed(() => {
  return formatCurrency(
    firstOffer.value?.totalSavedPrice,
    firstOffer.value?.offeredBooking.currency
  ).value
})

// HOTEL DETAIL
const isCurrenSelected = ref(true)
const hotelSearchResultStore = useHotelSearchResultsStore()

const searchOption = ref()
const customerCode = ref(featureFlag.customerCode)
const searchId = ref('')

const hotelDetailsIsOpen = ref(false)

const {
  data: hotelDetail,
  isFetching: hotelInfoIsFetching,
  isError,
} = useHotelInfoQuery(searchId, searchOption, customerCode, {
  enabled: hotelDetailsIsOpen,
})

function showHotelDetail(value: { searchId: string; searchOption: number }) {
  searchOption.value = value.searchOption
  searchId.value = value.searchId
  hotelDetailsIsOpen.value = true
}

watch(hotelDetail, (value) => {
  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  hotelSearchResultStore.setSelectedHotel(value.data)
})
</script>
